import React, {useEffect, useState} from "react";
import moment from "moment";
import {Box, IconButton, InputBase, Stack, TableCell, TableRow, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  Cancel01Icon, CheckmarkCircle02Icon, CircleIcon, Delete02Icon, MessagePreview01Icon, PencilEdit01Icon, Tick02Icon
} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import useConfirm from "../../../hooks/useConfirm";
import {MessageAnnotationsRepository} from "../../../repositories/MessageAnnotationRepository";
import {DiffView} from "../../project/message-annotation/components/DiffView";
import CompactAudioPlayer from "../../project/dashboard/components/CompactAudioPlayer";

export const cellSx = {py: 1, px: 1.5, fontSize: 13, color: "#23264D", borderBottom: "1px solid #F2F2F7", verticalAlign: "top"};

// Error rate pill, tinted by how far the transcription was off (0 = identical)
const ScorePill = ({value}) => {
  if (value === null || value === undefined) return <Typography sx={{fontSize: 12, color: "#B4B4C3"}}>—</Typography>;
  const style = value < 0.1
    ? {bg: "#E8F7EF", color: "#1E8A55"}
    : value < 0.3 ? {bg: "#FDF4E3", color: "#A86A0C"} : {bg: "#FDECEC", color: "#C73A3A"};
  return (
    <Box component={"span"} sx={{
      px: 1, py: 0.25, borderRadius: "999px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
      fontVariantNumeric: "tabular-nums", bgcolor: style.bg, color: style.color,
    }}>
      {Number(value).toFixed(2)}
    </Box>
  );
};

const iconButtonSx = {borderRadius: "50% !important", color: "#8A8AA0", "&:hover": {bgcolor: "#F4F4FA", color: "#4A4A63"}};

// One annotation: the transcription, its correction (editable, or as a diff), error rates, audio and actions.
export const AnnotationRow = ({annotation, language, diff, onUpdated, onDeleted, onOpenConversation}) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const confirmDelete = useConfirm(
    "Delete annotation",
    "The annotation and its copied audio are deleted, and the message is no longer annotated."
  );

  // the diff view is read-only
  useEffect(() => {
    if (diff) setEditing(false);
  }, [diff]);

  const startEdit = () => {
    setText(annotation.annotatedText || "");
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const response = await MessageAnnotationsRepository.createOrUpdate({
        messageId: annotation.messageId,
        annotatedText: text,
      });
      toast.success("Annotation updated");
      setEditing(false);
      onUpdated(response?.data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to update annotation");
    } finally {
      setSaving(false);
    }
  };

  const toggleResolved = async () => {
    try {
      const response = await MessageAnnotationsRepository.toggleResolved(annotation.id);
      onUpdated(response?.data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to update annotation");
    }
  };

  const remove = async () => {
    if (!(await confirmDelete())) return;
    try {
      await MessageAnnotationsRepository.deleteById(annotation.id);
      toast.success("Annotation deleted");
      onDeleted(annotation);
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete annotation");
    }
  };

  // the annotation's copy of the message audio, by annotation id, downloaded on the first play (the DTO does not
  // say whether there is one: the player shows "Audio not available" when the download fails)
  const audioRequest = annotation.id
    ? ({signal}) => MessageAnnotationsRepository.getAudio({id: annotation.id, signal})
    : null;

  return (
    <TableRow hover sx={{
      bgcolor: annotation.resolved ? "#FBFBFD" : "transparent",
      "&.MuiTableRow-hover:hover": {bgcolor: "#FAFAFD"},
      "&:last-of-type td": {borderBottom: 0},
    }}>
      <TableCell sx={{...cellSx, pl: 2.5, minWidth: 200, color: annotation.resolved ? "#8A8AA0" : "#4A4A63"}}>
        {annotation.originalText || <Typography component={"span"} sx={{fontSize: 12, color: "#B4B4C3"}}>No transcription</Typography>}
      </TableCell>

      <TableCell sx={{...cellSx, minWidth: 260}}>
        {editing ? (
          <Stack direction={"row"} alignItems={"flex-start"} gap={0.5}>
            <InputBase
              multiline
              autoFocus
              fullWidth
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setEditing(false);
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save();
              }}
              sx={{
                fontSize: 13, px: 1.25, py: 0.75, borderRadius: "10px", border: "1px solid #8491F7", bgcolor: "#fff",
              }}
            />
            <Tooltip title={"Cancel (Esc)"}>
              <IconButton size={"small"} sx={iconButtonSx} onClick={() => setEditing(false)}>
                <HugeiconsIcon icon={Cancel01Icon} size={16}/>
              </IconButton>
            </Tooltip>
            <Tooltip title={"Save (Ctrl+Enter)"}>
              <span>
                <IconButton size={"small"} disabled={saving} onClick={save}
                            sx={{...iconButtonSx, color: "#fff", bgcolor: "#23264D", "&:hover": {bgcolor: "#33376B"}}}>
                  <HugeiconsIcon icon={Tick02Icon} size={16}/>
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ) : diff ? (
          <DiffView originalText={annotation.originalText} annotatedText={annotation.annotatedText}/>
        ) : (
          <Stack direction={"row"} alignItems={"flex-start"} gap={0.5} sx={{"&:hover .edit-button": {opacity: 1}}}>
            <Typography sx={{flex: 1, fontSize: 13, fontWeight: 500, color: "#23264D"}}>{annotation.annotatedText || "—"}</Typography>
            <Tooltip title={"Edit correction"}>
              <IconButton className={"edit-button"} size={"small"} onClick={startEdit}
                          sx={{...iconButtonSx, opacity: 0.4, transition: "opacity 0.15s", mt: -0.5}}>
                <HugeiconsIcon icon={PencilEdit01Icon} size={16}/>
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </TableCell>

      <TableCell sx={{...cellSx, whiteSpace: "nowrap"}}>
        {language ? (
          <Tooltip title={language.languageName || ""}>
            <Box component={"span"} sx={{
              px: 0.75, py: 0.25, borderRadius: "6px", fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
              textTransform: "uppercase", color: "#4A4A63", bgcolor: "#F2F2F7",
            }}>
              {language.languageCode || language.languageName}
            </Box>
          </Tooltip>
        ) : <Typography sx={{fontSize: 12, color: "#B4B4C3"}}>—</Typography>}
      </TableCell>
      <TableCell sx={cellSx}><ScorePill value={annotation.cerScore}/></TableCell>
      <TableCell sx={cellSx}><ScorePill value={annotation.werScore}/></TableCell>
      <TableCell sx={cellSx}>
        {audioRequest ? <CompactAudioPlayer audioKey={`annotation-${annotation.id}`} request={audioRequest}/> : <Typography sx={{fontSize: 12, color: "#B4B4C3"}}>—</Typography>}
      </TableCell>
      <TableCell sx={{...cellSx, whiteSpace: "nowrap"}}>
        <Typography sx={{fontSize: 13, lineHeight: 1.3}}>{moment(annotation.dateCreated).format("DD MMM YYYY")}</Typography>
        <Typography sx={{fontSize: 11, color: "#8A8AA0", lineHeight: 1.3}}>{moment(annotation.dateCreated).format("HH:mm")}</Typography>
      </TableCell>

      <TableCell sx={{...cellSx, pr: 2.5, whiteSpace: "nowrap"}}>
        <Stack direction={"row"} gap={0.25}>
          <Tooltip title={annotation.resolved ? "Mark as unresolved" : "Mark as resolved"}>
            <IconButton size={"small"} onClick={toggleResolved}
                        sx={{...iconButtonSx, ...(annotation.resolved ? {color: "#1E8A55"} : {})}}>
              <HugeiconsIcon icon={annotation.resolved ? CheckmarkCircle02Icon : CircleIcon} size={17}/>
            </IconButton>
          </Tooltip>
          {annotation.conversationId && (
            <Tooltip title={`Open conversation #${annotation.conversationId}`}>
              <IconButton size={"small"} sx={iconButtonSx} onClick={() => onOpenConversation(annotation)}>
                <HugeiconsIcon icon={MessagePreview01Icon} size={17}/>
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={"Delete annotation"}>
            <IconButton size={"small"} sx={{...iconButtonSx, "&:hover": {bgcolor: "#FDECEC", color: "#C73A3A"}}} onClick={remove}>
              <HugeiconsIcon icon={Delete02Icon} size={17}/>
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
};
